import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  FileCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users
} from 'lucide-react';
import { mockAudits } from '../mock/mockData';

const AuditTracking = () => {
  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700',
      'in-progress': 'bg-blue-100 text-blue-700',
      planning: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Audit Tracking</h1>
        <p className="text-gray-600">
          30+ RBI/FIU audits delivered with zero-error reporting accuracy
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <FileCheck className="w-8 h-8 text-blue-600" />
                <Badge variant="secondary">+5</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">23</p>
              <p className="text-sm text-gray-600 mt-1">Active Audits</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <Badge className="bg-green-100 text-green-700">100%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">156</p>
              <p className="text-sm text-gray-600 mt-1">Completed Audits</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Clock className="w-8 h-8 text-yellow-600" />
                <Badge variant="secondary">This Quarter</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">8</p>
              <p className="text-sm text-gray-600 mt-1">Upcoming Audits</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                <Badge className="bg-purple-100 text-purple-700">+12%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">98.7%</p>
              <p className="text-sm text-gray-600 mt-1">Accuracy Rate</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Audits */}
        <Card>
          <CardHeader>
            <CardTitle>Active Audit Engagements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockAudits.map((audit) => (
                <Card
                  key={audit.id}
                  className="hover:shadow-md transition-all border-2 hover:border-blue-300"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {audit.client}
                          </h3>
                          <Badge className={getStatusColor(audit.status)}>
                            {audit.status.replace('-', ' ')}
                          </Badge>
                        </div>

                        <p className="text-sm text-gray-600 mb-3">{audit.type}</p>

                        <div className="space-y-2">
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Clock className="w-4 h-4" />
                              <span>
                                {audit.startDate} - {audit.endDate}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Users className="w-4 h-4" />
                              <span>Lead: {audit.lead}</span>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-600">Progress</span>
                              <span className="text-sm font-semibold text-gray-900">
                                {audit.progress}%
                              </span>
                            </div>
                            <Progress value={audit.progress} className="h-2" />
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline">
                          View Details
                        </Button>
                        <Button size="sm">Update Progress</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Audit Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Types Distribution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">RBI Statutory Audit</span>
                  <span className="text-sm font-semibold text-gray-900">45%</span>
                </div>
                <Progress value={45} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">FIU-IND Compliance</span>
                  <span className="text-sm font-semibold text-gray-900">30%</span>
                </div>
                <Progress value={30} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Internal Audit</span>
                  <span className="text-sm font-semibold text-gray-900">15%</span>
                </div>
                <Progress value={15} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Concurrent Audit</span>
                  <span className="text-sm font-semibold text-gray-900">10%</span>
                </div>
                <Progress value={10} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Audit Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Zero-Error Reports</p>
                      <p className="text-2xl font-bold text-gray-900">100%</p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg. Completion Time</p>
                      <p className="text-2xl font-bold text-gray-900">42 days</p>
                    </div>
                    <Clock className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Client Satisfaction</p>
                      <p className="text-2xl font-bold text-gray-900">4.9/5.0</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AuditTracking;
